import React, { useState } from 'react';
import Badge from '../ui/Badge';
import './Sidebar.css';

/**
 * Sidebar — primary navigation rail (collapsible).
 *
 * Replaces the old top Navigation bar. Holds the primary destinations plus a
 * collapsible "Staff" group for admins/mods. Collapsing shrinks it to an icon
 * rail; on mobile it becomes an off-canvas drawer (toggled from the TopBar).
 * Part of the app-shell redesign (docs/frontend/DESIGN_REDESIGN.md §6.1/§6.3).
 *
 * @param {string} currentPage
 * @param {function} onNavigate
 * @param {boolean} isAdmin
 * @param {boolean} isModerator
 * @param {boolean} isContentCreator - content tools and suggestions only
 * @param {object} dateGenStatus - weekly date-generation balance; shown in the
 *   mobile drawer since the TopBar pill is hidden on narrow phones
 * @param {number} expeditionsToCollect
 * @param {number} competitionRewardsToClaim
 * @param {number} tradeProposalsPending
 * @param {boolean} collapsed - icon-only rail
 * @param {function} onToggleCollapse
 * @param {boolean} mobileOpen - drawer open on small screens
 * @param {function} onCloseMobile
 */
function Sidebar({
  currentPage,
  onNavigate,
  isAdmin,
  isModerator,
  isContentCreator,
  dateGenStatus,
  expeditionsToCollect,
  competitionRewardsToClaim,
  tradeProposalsPending,
  boostersEnabled = false,
  collapsed,
  onToggleCollapse,
  mobileOpen,
  onCloseMobile
}) {
  const [staffOpen, setStaffOpen] = useState(false);

  const navItems = [
    { id: 'home', label: 'Home', icon: '🏠' },
    { id: 'collection', label: 'Collection', icon: '🎴', tutorialId: 'nav-collection' },
    {
      id: 'trades',
      label: 'Trade',
      icon: '🔄',
      badge: tradeProposalsPending > 0 ? tradeProposalsPending : null,
      tutorialId: 'nav-trades'
    },
    {
      id: 'expeditions-hub',
      label: 'Expedition',
      icon: '🗺️',
      badge: expeditionsToCollect > 0 ? expeditionsToCollect : null,
      tutorialId: 'nav-expeditions'
    },
    // Feature-flagged: hidden from players until BOOSTERS_ENABLED is on server-side
    ...(boostersEnabled
      ? [
          {
            id: 'boosters',
            label: 'Boosters',
            icon: '🎴',
            // Same card glyph as Collection — tinted green so the two entries
            // are told apart at a glance (see .sb-icon-booster in Sidebar.css)
            iconClass: 'sb-icon-booster',
            tutorialId: 'nav-boosters'
          }
        ]
      : []),
    {
      id: 'minigames',
      label: 'Mini games',
      icon: '🎮',
      badge: competitionRewardsToClaim > 0 ? competitionRewardsToClaim : null,
      tutorialId: 'nav-minigames'
    },
    { id: 'galleries', label: 'Galleries', icon: '🖼️', tutorialId: 'nav-galleries' },
    { id: 'reference', label: 'Reference', icon: '📖', tutorialId: 'nav-reference' }
  ];

  const staffItems = [];
  if (isAdmin) {
    staffItems.push({ id: 'credits-admin', label: 'Credits', icon: '💰' });
  }
  if (isAdmin || isModerator) {
    staffItems.push({ id: 'proposals-admin', label: 'Suggestions', icon: '🔧' });
    staffItems.push({ id: 'stats', label: 'Stats', icon: '📊' });
    staffItems.push({ id: 'moderator', label: 'Moderator Tools', icon: '🛡️' });
    staffItems.push({ id: 'task-board', label: 'Task Board', icon: '📋' });
  } else if (isContentCreator) {
    staffItems.push({ id: 'proposals-admin', label: 'Suggestions', icon: '🔧' });
    staffItems.push({ id: 'moderator', label: 'Content Tools', icon: '🛡️' });
  }
  if (isAdmin) {
    staffItems.push({ id: 'admin-tools', label: 'Admin Tools', icon: '🔐' });
    staffItems.push({ id: 'design-lab', label: 'Design Lab', icon: '🎨' });
    staffItems.push({ id: 'production-proposals', label: 'Prod Proposals', icon: '🔄' });
  }

  const handleNavigate = (id) => {
    onNavigate(id);
    if (onCloseMobile) onCloseMobile();
  };

  const renderItem = (item, extraClass = '') => (
    <button
      key={item.id}
      className={`sb-item ${extraClass} ${currentPage === item.id ? 'sb-item-active' : ''}`}
      onClick={() => handleNavigate(item.id)}
      data-tutorial={item.tutorialId}
      title={collapsed ? item.label : undefined}
      aria-label={item.label}
      aria-current={currentPage === item.id ? 'page' : undefined}
    >
      <span className={`sb-icon ${item.iconClass || ''}`}>{item.icon}</span>
      <span className="sb-label">{item.label}</span>
      {item.badge != null && (
        <Badge variant="success" size="sm" className="sb-badge">
          {item.badge}
        </Badge>
      )}
    </button>
  );

  return (
    <>
      {mobileOpen && <div className="sb-scrim" onClick={onCloseMobile} aria-hidden="true" />}
      <aside
        aria-label="Primary"
        className={`app-sidebar ${collapsed ? 'app-sidebar-collapsed' : ''} ${
          mobileOpen ? 'app-sidebar-mobile-open' : ''
        }`}
      >
        <div className="sb-brand">
          <button
            className="sb-collapse-btn"
            onClick={onToggleCollapse}
            title={collapsed ? 'Expand menu' : 'Collapse menu'}
            aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
          >
            ☰
          </button>
          <span className="sb-brand-name">Aesthetic Legends</span>
        </div>

        {/* Date-gen balance — only surfaces here in the mobile drawer, where the
            TopBar pill is hidden to keep the menu button tappable. */}
        {dateGenStatus && (
          <div className="sb-date-gens" title="Weekly date generations remaining">
            🎨{' '}
            {dateGenStatus.unlimited ? (
              <span>∞</span>
            ) : (
              <span>
                {dateGenStatus.weekly_remaining}/{dateGenStatus.weekly_cap}
                {dateGenStatus.bonus_remaining > 0 && (
                  <span className="sb-date-gens-bonus"> +{dateGenStatus.bonus_remaining}</span>
                )}
              </span>
            )}
          </div>
        )}

        <nav className="sb-nav" aria-label="Main navigation">
          {navItems.map((item) => renderItem(item))}
        </nav>

        {staffItems.length > 0 && (
          <div className="sb-staff">
            <button
              className="sb-staff-toggle"
              onClick={() => setStaffOpen((o) => !o)}
              title="Staff tools"
              aria-label="Staff tools"
              aria-expanded={staffOpen}
              aria-controls="sb-staff-items"
            >
              <span className="sb-icon">⚙️</span>
              <span className="sb-label">Staff</span>
              <span className="sb-staff-caret">{staffOpen ? '▾' : '▸'}</span>
            </button>
            {/* Always rendered (hidden when closed) so the toggle's
                aria-controls="sb-staff-items" always resolves to a real node. */}
            <div
              id="sb-staff-items"
              role="group"
              aria-label="Staff tools"
              className="sb-staff-items"
              hidden={!staffOpen}
            >
              {staffItems.map((item) => renderItem(item, 'sb-item-staff'))}
            </div>
          </div>
        )}
      </aside>
    </>
  );
}

export default Sidebar;
